import { Component, Inject, OnInit, Optional } from '@angular/core';
import { TtScreenBlockService } from '@app/core/components/tt-screen-block/tt-screen-block.service';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { UtilityService } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2DataTaskItem, P2DataTaskService, P2DataTaskSettingsGet } from '@app/services/p2-data-task.service';

@Component({
    selector: 'tt-p2-data-task',
    templateUrl: './p2-data-task.component.html',
    styleUrls: ['./p2-data-task.component.css'],
    standalone: false,
})
export class P2DataTaskComponent implements OnInit {
    public setting?: P2DataTaskSettingsGet;
    public procedures: P2DataTaskItem[] = [];
    public itemState = '';
    public selectedDataTaskKeyno = '';
    public readonly tabletSize: MediaQueryList;

    public readonly searchTextButtons: FormButton[] = [
        { id: 'erase', icon: 'fas fa-eraser', type: 'danger', onClick: () => this.clearSearchText() },
        { id: 'calculator', icon: 'fas fa-calculator', type: 'primary', onClick: () => void this.openCalculator() },
        { id: 'search', icon: 'fas fa-search', type: 'primary', onClick: () => void this.searchItems() },
    ];

    private navigator?: TTViewNavigator;

    constructor(
        private dataTask: P2DataTaskService,
        private screenBlock: TtScreenBlockService,
        private remember: RememberService,
        private modal: ModalService,
        private utility: UtilityService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async loadProcedures(): Promise<void> {
        const searchtext = this.setting?.searchtext?.trim() ?? '';
        if (!searchtext) {
            this.procedures = [];
            this.itemState = '';
            return;
        }

        this.screenBlock.start();
        try {
            this.procedures = await this.dataTask.list({ searchtext });
            this.itemState = this.procedures.find((item) => !!item.item_state)?.item_state ?? '';
        } finally {
            this.screenBlock.stop();
        }
    }

    public async searchItems(): Promise<void> {
        const searchtext = this.setting?.searchtext?.trim() ?? '';
        if (searchtext) await this.remember.remember('w_p2datatask.searchtext', searchtext);
        await this.loadProcedures();
    }

    public async onSearchTextChanged(value: string): Promise<void> {
        if (value !== '') return;

        await this.remember.remember('w_p2datatask.searchtext', '');
        await this.loadProcedures();
    }

    public async clearSearchText(): Promise<void> {
        if (!this.setting) return;

        this.setting.searchtext = '';
        await this.onSearchTextChanged('');
    }

    public async openCalculator(): Promise<void> {
        if (!this.setting) return;

        const value = await this.modal.showCalculator(this.setting.searchtext);
        if (typeof value === 'string') this.setting.searchtext = value;
        await this.searchItems();
    }

    public openDataTask(item: P2DataTaskItem, event?: MouseEvent): void {
        const keyno = `${item.item_id}`;
        if (this.tabletSize.matches && this.itemState === 'p2datataskedit') {
            this.selectedDataTaskKeyno = keyno;
            return;
        }

        this.navigateToEdit(keyno, event);
    }

    public createDataTask(event?: MouseEvent): void {
        if (this.tabletSize.matches && this.itemState === 'p2datataskedit') {
            this.selectedDataTaskKeyno = '0';
            return;
        }

        this.navigateToEdit('0', event);
    }

    public ngOnInit(): void {
        void this.init();
    }

    private async init(): Promise<void> {
        this.setting = await this.dataTask.getSettings();
        this.setting.searchtext ??= '';
        await this.loadProcedures();
    }

    private navigateToEdit(keyno: string, event?: MouseEvent): void {
        const target = { to: 'p2datataskedit-test', parms: { p2_datatask_keyno: keyno } };
        if (event && this.navigator && this.utility.isModifierKeyPressed(event)) {
            void this.navigator.newTab(target);
        } else {
            void this.navigator?.go(target);
        }
    }
}
